// Helpers shared by the SodistoreHome / Sodistore Pro battery views.
//
// Both products log the SAME Pylontech BMS record in the S3 chunks:
//
//   BatteryRecord.Cluster{n}.Battery{m} = { Valid, MinCellVoltageV, ... CellVoltagesV }
//
// but their InverterRecord shapes differ: Growatt is flat (Battery1Soc, ...) while the
// inesco 12K / Sinexcel nests everything under Devices["1"]. Dispatch on the record
// SHAPE (`Devices` present) and never on Installation.Device — verified on live data,
// the same product can carry either inverter.
//
// Cluster numbering is assumed FLAT across the installation: an installation with 3
// inverters of 2 clusters reports Cluster1..Cluster6, matching Config.PylontechClusters,
// which is one entry per serial port on the datalogger.
//
// ⚠ VERIFIED ONLY FOR A SINGLE INVERTER. The measurement behind this (a cluster's
// ModuleVoltageV matching its inverter battery input to 58.934 vs 58.93) was taken on a
// one-inverter site, where flat numbering and per-inverter numbering are the same thing
// and so cannot be told apart. On a multi-inverter Pro site the two disagree — inverter
// 2's first input is Cluster3 under flat numbering, Cluster1 under per-inverter — and if
// the assumption is wrong the view shows another cluster's cells under this cluster's
// name, with nothing to signal it. Confirm against a live multi-inverter chunk before
// relying on this view there.
//
// The flat index comes from getActiveClusters, which counts EVERY configured slot,
// including ones with no serial numbers entered. So it also assumes the admin-entered SN
// tree has the same shape as the datalogger's port list; a gap in one and not the other
// shifts every later cluster.

// Every numeric field is nullable: a battery whose BMS link is down reports
// Valid:false with all values null (observed continuously on the Growatt prototype).
export interface PylontechModule {
  Valid: boolean;
  MinCellVoltageV: number | null;
  MaxCellVoltageV: number | null;
  CellVoltageDiffV: number | null;
  ModuleVoltageV: number | null;
  CurrentA: number | null;
  RemainCapacityAh: number | null;
  FullCapacityAh: number | null;
  CycleCount: number | null;
  CellVoltagesV: Record<string, number> | null;
}

export interface ClusterModule {
  id: number;
  module: PylontechModule;
}

export interface InverterBattery {
  soc: number | null;
  soh: number | null;
  temperature: number | null;
  power: number | null;
  voltage: number | null;
  current: number | null;
  state: string | null;
  bmsState: string | null;
  alarms: string[];
  // Growatt exposes no per-battery alarm bits, only installation-wide fault/warning
  // codes. The view labels them differently so nobody reads them as per-battery.
  systemAlarms: boolean;
}

const num = (value: unknown): number | null =>
  typeof value === 'number' && isFinite(value) ? value : null;

const str = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

// Fault bits the inesco 12K reports per battery input. "Normal" means inactive.
// Deliberately excludes the LithiumBattery{n}Full / DischargeEnd status bits, which
// describe a normal charge state rather than a fault.
export const sinexcelAlarmKeys = (batteryIndex: number): string[] => [
  `Battery${batteryIndex}NotConnected`,
  `Battery${batteryIndex}Overvoltage`,
  `Battery${batteryIndex}Undervoltage`,
  `Battery${batteryIndex}DischargeEnd`,
  `Battery${batteryIndex}Inverted`,
  `Battery${batteryIndex}PowerTubeFault`,
  `Battery${batteryIndex}OverloadTimeout`,
  `Battery${batteryIndex}SoftStartFailure`,
  `Battery${batteryIndex}InsufficientPower`,
  `Battery${batteryIndex}BackupProhibited`,
  `Battery${batteryIndex}Overcurrent`,
  `Battery${batteryIndex}ChargingSealingWave`,
  `LithiumBattery${batteryIndex}ChargeForbidden`,
  `LithiumBattery${batteryIndex}DischargeForbidden`
];

// Strips the Battery{n} / LithiumBattery{n} prefix so one translation key serves both
// battery inputs: "Battery2Overvoltage" -> "Overvoltage".
export const alarmLabelKey = (alarmKey: string): string =>
  'batteryAlarm' + alarmKey.replace(/^(Lithium)?Battery\d+/, '');

// Modules of one cluster, ordered Battery1, Battery2, ... Empty when the cluster is
// absent from the record (installation not commissioned yet, or fewer clusters wired
// than the installation model claims).
export const getClusterModules = (
  batteryRecord: any,
  flatClusterIdx: number
): ClusterModule[] => {
  const cluster = batteryRecord?.[`Cluster${flatClusterIdx + 1}`];
  if (!cluster || typeof cluster !== 'object') return [];

  // Anchored match rather than a strip-and-parse: parseInt('12V'.replace(...)) returns 12
  // for a key that is not a module at all, so a future summary field on the cluster object
  // would render as a phantom battery — or collide with a real module's id and hide it.
  return Object.entries(cluster)
    .map(([key, module]) => ({
      match: /^Battery(\d+)$/.exec(key),
      module: module as PylontechModule
    }))
    .filter((entry) => entry.match != null && entry.module != null)
    .map((entry) => ({
      id: parseInt(entry.match![1], 10),
      module: entry.module
    }))
    .sort((a, b) => a.id - b.id);
};

export const hasBmsData = (module: PylontechModule | undefined): boolean =>
  !!module && module.Valid === true;

export interface CellVoltage {
  // Cell number as the BMS labels it — carried through so a missing or unparsable cell
  // cannot shift the numbering of every tile after it.
  index: number;
  value: number;
}

// Cell voltages ordered Cell1..CellN (15 on current hardware). Object key order is not
// guaranteed, so sort on the numeric suffix rather than trusting insertion order.
export const getCellVoltages = (module: PylontechModule): CellVoltage[] => {
  if (!module.CellVoltagesV) return [];
  // Anchored for the same reason as getClusterModules: a non-cell key must not become a
  // phantom tile, and must not take the numeric slot of a real cell.
  return Object.entries(module.CellVoltagesV)
    .map(([key, value]) => ({ match: /^Cell(\d+)$/.exec(key), value }))
    .filter((cell) => cell.match != null && typeof cell.value === 'number')
    .map((cell) => ({ index: parseInt(cell.match![1], 10), value: cell.value }))
    .sort((a, b) => a.index - b.index);
};

const sinexcelBattery = (device: any, batteryIndex: number): InverterBattery => ({
  // SocFromBms is the BMS reading, so it lines up with the Pylontech record shown
  // next to it; Battery{n}Soc is the inverter's own estimate and only a fallback.
  soc:
    num(device?.[`Battery${batteryIndex}SocFromBms`]) ??
    num(device?.[`Battery${batteryIndex}Soc`]),
  soh: num(device?.[`Battery${batteryIndex}Soh`]),
  temperature: num(device?.[`Battery${batteryIndex}Temperature`]),
  power: num(device?.[`Battery${batteryIndex}Power`]),
  // PackTotalVoltage (BMS) is a different quantity from Battery{n}Voltage (inverter DC
  // side) — they read ~0.1 V apart. Never fall back from one to the other.
  voltage: num(device?.[`Battery${batteryIndex}PackTotalVoltage`]),
  current: num(device?.[`Battery${batteryIndex}PackTotalCurrent`]),
  state: str(device?.[`Battery${batteryIndex}OperatingState`]),
  bmsState: str(device?.[`Battery${batteryIndex}BmsState`]),
  alarms: sinexcelAlarmKeys(batteryIndex).filter(
    (key) => str(device?.[key]) != null && device[key] !== 'Normal'
  ),
  systemAlarms: false
});

const growattBattery = (inverter: any, batteryIndex: number): InverterBattery => {
  const codes = [inverter?.FaultMainCode, inverter?.WarningMainCode]
    .map(str)
    .filter((code): code is string => code != null && code !== 'NoAlarm');
  // GrowattErrorCode and GrowattWarningCode overlap (OverTemperature,
  // OffGridOutputOverload), so the same name can arrive twice.
  const uniqueCodes = Array.from(new Set(codes));

  return {
    soc: num(inverter?.[`Battery${batteryIndex}Soc`]),
    soh: num(inverter?.[`Battery${batteryIndex}Soh`]),
    temperature: num(inverter?.[`Battery${batteryIndex}AmbientTemperature`]),
    power: num(inverter?.[`Battery${batteryIndex}Power`]),
    voltage: num(inverter?.[`Battery${batteryIndex}Voltage`]),
    current: num(inverter?.[`Battery${batteryIndex}Current`]),
    state: str(inverter?.BatteryOperatingMode),
    bmsState: null,
    alarms: uniqueCodes,
    systemAlarms: true
  };
};

// Inverter-side view of one battery cluster. clIdx is the cluster's index WITHIN its
// inverter, which is also the inverter's battery input number.
export const getInverterBattery = (
  inverterRecord: any,
  invIdx: number,
  clIdx: number
): InverterBattery => {
  const batteryIndex = clIdx + 1;

  if (inverterRecord?.Devices) {
    return sinexcelBattery(
      inverterRecord.Devices[String(invIdx + 1)],
      batteryIndex
    );
  }
  return growattBattery(inverterRecord, batteryIndex);
};

export const CELL_GREEN = '#32CD32';
export const CELL_AMBER = '#ffbf00';
export const CELL_RED = '#FF033E';

// The battery views already treat a 32-63 V pack as healthy; across a 15-cell module
// that is ~2.13-4.20 V per cell. A cell outside that is flagged regardless of balance.
export const CELL_MIN_V = 2.13;
export const CELL_MAX_V = 4.2;
// Spread between the weakest and strongest cell of one module. Measured on live
// hardware: 0.005 V on a healthy module, 0.119 V on one carrying a weak cell, and
// 0.22 V on the worst module in the reference samples.
export const CELL_IMBALANCE_V = 0.1;

export interface CellReading extends CellVoltage {
  color: string;
}

// Colour of one cell tile. Absolute limits first, then — only once the module is
// genuinely out of balance — the weakest cell is flagged red and the strongest amber,
// so a healthy pack shows all green instead of permanently marking its own min/max.
const colorFor = (
  voltage: number,
  min: number,
  max: number,
  imbalanced: boolean
): string => {
  if (voltage < CELL_MIN_V || voltage > CELL_MAX_V) return CELL_RED;
  if (imbalanced) {
    if (voltage === min) return CELL_RED;
    if (voltage === max) return CELL_AMBER;
  }
  return CELL_GREEN;
};

// Extremes and the imbalance test are computed from the cell array itself, NOT from the
// separately-reported Min/MaxCellVoltageV/CellVoltageDiffV fields: comparing a reading
// against a differently-rounded copy of itself with === would silently colour an
// imbalanced module all-green, which reads as a false all-clear.
export const getCellReadings = (module: PylontechModule): CellReading[] => {
  const cells = getCellVoltages(module);
  if (cells.length === 0) return [];

  const values = cells.map((cell) => cell.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const imbalanced = max - min > CELL_IMBALANCE_V;

  return cells.map((cell) => ({
    ...cell,
    color: colorFor(cell.value, min, max, imbalanced)
  }));
};
